/**
 * ============================================================
 * Apple-Style Homepage — Scroll Reveal & Interactions
 * File: assets/apple-homepage.js
 * ============================================================
 */

(function () {
  'use strict';

  // ============================================================
  // SCROLL REVEAL
  // Uses IntersectionObserver to trigger .aph-revealed on
  // elements with the .aph-scroll-reveal class
  // ============================================================
  function initScrollReveal() {
    const elements = document.querySelectorAll('.aph-scroll-reveal');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            // Stagger sibling items in the same parent grid
            const siblings = entry.target.parentElement
              ? Array.from(entry.target.parentElement.querySelectorAll('.aph-scroll-reveal:not(.aph-revealed)'))
              : [];
            const index = siblings.indexOf(entry.target);
            const delay = index >= 0 ? index * 80 : 0; // 80ms stagger

            setTimeout(function () {
              entry.target.classList.add('aph-revealed');
            }, delay);

            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,      // trigger when 12% visible
        rootMargin: '0px 0px -60px 0px'  // fire slightly before reaching edge
      }
    );

    elements.forEach(function (el) {
      observer.observe(el);
    });
  }

  // ============================================================
  // HERO PARALLAX (subtle)
  // Moves the hero background at a reduced rate on scroll
  // to create a depth effect
  // ============================================================
  function initHeroParallax() {
    const hero = document.getElementById('aph-hero');
    if (!hero) return;

    const bg = hero.querySelector('.aph-hero__bg, .aph-hero__video');
    if (!bg) return;

    let ticking = false;

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(function () {
          const scrollY = window.pageYOffset;
          const heroHeight = hero.offsetHeight;
          if (scrollY < heroHeight) {
            const parallaxAmount = scrollY * 0.35;
            bg.style.transform = 'translateY(' + parallaxAmount + 'px)';
          }
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ============================================================
  // HERO SCROLL HINT — hide after user scrolls a bit
  // ============================================================
  function initScrollHint() {
    const hint = document.querySelector('.aph-hero__scroll-hint');
    if (!hint) return;

    function checkScroll() {
      if (window.pageYOffset > 80) {
        hint.style.opacity = '0';
        hint.style.pointerEvents = 'none';
      } else {
        hint.style.opacity = '1';
      }
    }

    window.addEventListener('scroll', checkScroll, { passive: true });
  }

  // ============================================================
  // FEATURE IMAGE HOVER 3D TILT
  // Subtle 3D tilt on feature images on mouse move
  // ============================================================
  function initImageTilt() {
    const images = document.querySelectorAll('.aph-feature__image');
    if (!images.length) return;

    images.forEach(function (img) {
      const card = img.closest('.aph-feature__media');
      if (!card) return;

      card.addEventListener('mousemove', function (e) {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        img.style.transform = 'perspective(800px) rotateY(' + (x * 6) + 'deg) rotateX(' + (-y * 4) + 'deg) translateY(-6px) scale(1.02)';
      });

      card.addEventListener('mouseleave', function () {
        img.style.transform = '';
      });
    });
  }

  // ============================================================
  // SMOOTH ANCHOR SCROLLING
  // For any anchor links pointing to #ids on the page
  // ============================================================
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // ============================================================
  // PRODUCT CARD — quick buy animation
  // Adds a subtle pulse when hovering shop button
  // ============================================================
  function initProductCards() {
    const cards = document.querySelectorAll('.aph-product-card');
    cards.forEach(function (card) {
      card.addEventListener('mouseenter', function () {
        const arrow = card.querySelector('.aph-link-arrow');
        if (arrow) {
          arrow.style.color = '#0071e3';
        }
      });
      card.addEventListener('mouseleave', function () {
        const arrow = card.querySelector('.aph-link-arrow');
        if (arrow) {
          arrow.style.color = '';
        }
      });
    });
  }

  // ============================================================
  // STATS COUNTER ANIMATION
  // Animates numeric stats from 0 to their target value
  // ============================================================
  function animateCounters() {
    const numbers = document.querySelectorAll('.aph-stats__number');
    if (!numbers.length) return;

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);

        const el = entry.target;
        const raw = el.textContent.trim();

        // Extract leading number + suffix (e.g., "10K+", "98%", "5★")
        const match = raw.match(/^([\d.]+)(.*)/);
        if (!match) return;

        const target = parseFloat(match[1]);
        const suffix = match[2];
        const duration = 1200;
        const start = performance.now();

        function update(now) {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
          const current = target * eased;

          // Format nicely
          const formatted = Number.isInteger(target)
            ? Math.round(current).toLocaleString()
            : current.toFixed(1);

          el.textContent = formatted + suffix;

          if (progress < 1) {
            requestAnimationFrame(update);
          }
        }

        requestAnimationFrame(update);
      });
    }, { threshold: 0.5 });

    numbers.forEach(function (el) {
      observer.observe(el);
    });
  }

  // ============================================================
  // INIT — run everything when DOM is ready
  // ============================================================
  function init() {
    // Respect prefers-reduced-motion
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    initScrollReveal();
    initScrollHint();
    initSmoothScroll();
    initProductCards();
    animateCounters();

    if (!reducedMotion) {
      initHeroParallax();
      initImageTilt();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();